// CORS 문제 해결된 axios 인스턴스

import axios from 'axios';
import fetchCsrfToken, { invalidateCsrfToken, refreshCsrfToken } from './csrfTokenSlice';
import { getApiErrorStatus } from 'src/types/api';


// CSRF 자동 헤더 설정이 포함된 axios 인스턴스 생성
const axiosInstance = axios.create({
  withCredentials: true, // 쿠키 포함 설정
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  },
  // timeout: 15000 // 15초 타임아웃
});

// 토큰 캐시 관리를 위한 변수
let cachedToken: string | null = null;
let tokenPromise: Promise<string | null> | null = null;

// 로그인 체크 중복 호출 방지 플래그
let isCheckingLogin = false;

// 캐시된 토큰 가져오기 (API 호출 최소화)
const getCachedCsrfToken = async (): Promise<string | null> => {
  // 이미 캐시된 토큰이 있으면 바로 반환
  if (cachedToken) {
    return cachedToken;
  }

  // 이미 토큰 요청 중이면 해당 Promise 반환
  if (tokenPromise) {
    return tokenPromise;
  }

  // 새로운 토큰 요청
  tokenPromise = fetchCsrfToken();
  
  try {
    const token = await tokenPromise;
    if (token) {
      cachedToken = token;
    }
    return token;
  } finally {
    tokenPromise = null;
  }
};

// 토큰 캐시 무효화
const clearCachedToken = () => {
  cachedToken = null;
  tokenPromise = null;
  invalidateCsrfToken();
};

// 로그인 상태 재확인 함수
const verifyLoginStatus = async (): Promise<boolean> => {
  // 이미 체크 중이면 건너뛰기 (무한 루프 방지)
  if (isCheckingLogin) {
    return false;
  }

  isCheckingLogin = true;

  try {
    // loginCheck API 호출 (baseURL 설정에 따라 경로 조정 필요)
    const response = await axios.get('/auth/login-check/', {
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
      }
    });

    isCheckingLogin = false;
    
    // 응답이 성공이고 인증되었으면 true 반환
    return response.data?.is_authenticated === true;
    
  } catch (error) {
    isCheckingLogin = false;
    
    // 401 에러면 확실히 세션 만료
    if (getApiErrorStatus(error) === 401) {
      return false;
    }
    
    // 다른 에러는 네트워크 문제일 수 있으므로 false 반환
    console.error('로그인 상태 확인 실패:', error);
    return false;
  }
};

// 요청 인터셉터에서 캐시된 토큰 사용
axiosInstance.interceptors.request.use(async (config) => {
  // GET 요청은 CSRF 토큰 불필요
  if (config.method === 'get') {
    return config;
  }

  const token = await getCachedCsrfToken();

  if (token) {
    config.headers['X-CSRFToken'] = token;
  } else {
    console.warn('CSRF 토큰이 없습니다. 요청이 실패할 수 있습니다.');
    // 토큰이 없으면 요청을 중단하고 에러를 던짐
    throw new Error('CSRF 토큰을 획득할 수 없습니다. 세션이 만료되었거나 서버에 연결할 수 없습니다.');
  }

  return config;
}, (error) => {
  console.error('요청 인터셉터 에러:', error);
  return Promise.reject(error);
});

// 응답 인터셉터에서 세션 만료 감지 및 토큰 재요청
axiosInstance.interceptors.response.use(
  (response) => {
    // 성공 응답은 그대로 반환
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // 401 Unauthorized 에러 처리 (세션 만료 가능성)
    if (error.response?.status === 401) {
      clearCachedToken();
      
      // 로그인 체크 API로 한 번 더 확인
      const isStillAuthenticated = await verifyLoginStatus();
      
      if (!isStillAuthenticated) {
        // 확실히 인증 만료됨 → 로그아웃 처리
        localStorage.removeItem('autoLoginData');
        
        // 커스텀 이벤트 발생 (App.tsx에서 감지)
        window.dispatchEvent(new CustomEvent('unauthorized'));
      }
      
      return Promise.reject(error);
    }

    // 403 Forbidden 에러이고, 아직 재시도하지 않은 경우
    if (
      error.response?.status === 403 &&
      !originalRequest._retry &&
      originalRequest.method !== 'get'
    ) { 
      // 재시도 플래그 설정 (무한 루프 방지)
      originalRequest._retry = true;
      
      // 기존 토큰 캐시 무효화
      clearCachedToken();
      
      try {
        // 새로운 토큰 요청 (캐시 무시)
        const newToken = await refreshCsrfToken();
        
        if (newToken) {
          // 새 토큰으로 원래 요청 재시도
          originalRequest.headers['X-CSRFToken'] = newToken;
          cachedToken = newToken; // 캐시 업데이트
          return axiosInstance(originalRequest);
        } else {
          // 토큰 획득 실패는 세션 만료를 의미함
          return Promise.reject(error);
        }
      } catch (tokenError) {
        console.error('CSRF 토큰 재요청 실패:', tokenError);
        // 토큰 재요청 실패 시 원래 에러 반환
        return Promise.reject(error);
      }
    }

    // 네트워크 에러 처리
    if (!error.response) {
      console.error('네트워크 에러 발생:', error.message);
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.error('⏱요청 타임아웃');
      }
    }

    // CORS 에러인 경우 특별 처리
    if (error.message && error.message.includes('CORS')) {
      console.error('CORS 에러 발생:', error.message);
    }

    // 5xx 서버 에러
    if (error.response?.status >= 500) {
      console.error('서버 에러:', error.response.status, error.response.data);
    }

    // 재시도 실패하거나 다른 에러인 경우 원래 에러 반환
    return Promise.reject(error);
  }
);

export default axiosInstance;

// 토큰 캐시 관리 함수들도 export
export { clearCachedToken, getCachedCsrfToken };